import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormLabel, Grid, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import moment, { Moment } from "moment";
import { useMemo } from "react";
import { Controller, FormProvider, useForm, useFormContext } from "react-hook-form";
import { formatarInputValorMonetário } from "../../../../../../helpers/masks";
import { TDadosPropriedade, TOpenModalPropriedade } from ".";
import PaperDraggable from "../../../../../../components/PaperDraggable";


export default function DialogFormDadosPropriedade({
    open,
    defaultValue,
    onClose,
    onSubmit,
}: DialogFormDadosPropriedadeProps) {

    const valoresIniciais = useMemo(() => defaultValue?.defaultValue ? defaultValue.defaultValue : null, [defaultValue])

    const methods = useForm<TDadosPropriedade>({
        defaultValues: valoresIniciais || {
            nome: "",
            propriaProp: "Não",
            tamanho: "",
            arrendatario: "Sim",
            vencimentoContrato: moment(),
            leite: { produtor: "Não", mediaDiaria: "", qtdVacas: "", valor: "" },
            cafe: { produtor: "Não", qtdSacas: "", qtdHa: "", valor: "" },
            milho: { produtor: "Não", qtdSacas: "", qtdHa: "", valor: "" },
            soja: { produtor: "Não", qtdSacas: "", qtdHa: "", valor: "" },
            gadoCorte: { produtor: "Não", qtdCabecas: "", raca: "", valor: "" },
            outraCultura: { produtor: "Não", nome: "", producaoEstimada: "", valor: "" },
            outraRenda: { produtor: "Não", nome: "", valor: "" },
        }
    })

    const { control, getValues, watch, handleSubmit } = methods;

    const possuiArendatarioComodatario = watch(`arrendatario`) === "Sim"

    const opcoesRadio = useMemo(() => [
        { value: "Sim", label: "Sim" },
        { value: "Não", label: "Não" },
    ], [])

    return (
        <FormProvider {...methods}>
            <Dialog
                open={Boolean(open)}
                onClose={onClose}
                fullWidth
                maxWidth="md"
                PaperComponent={PaperDraggable}
                aria-labelledby="draggable-dialog-title"
            >
                <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">Dados da propriedade</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} mt={0.5}>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Controller
                                name={`nome`}
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <TextField
                                        {...field}
                                        label="Nome"
                                        required
                                        variant="outlined"
                                        fullWidth
                                        error={invalid}
                                        helperText={error?.message || ""}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }} display="flex" justifyContent="center">
                            <Controller
                                name={`propriaProp`}
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field }) => (
                                    <FormControl {...field}>
                                        <FormLabel id="propriaProp">Própria?</FormLabel>
                                        <RadioGroup
                                            row
                                            aria-labelledby="propriaProp"
                                            defaultValue={getValues(`propriaProp`)}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                        >
                                            {
                                                opcoesRadio.map((opcao, index) => (
                                                    <FormControlLabel key={index} value={opcao.value} control={<Radio />} label={opcao.label} />
                                                ))
                                            }
                                        </RadioGroup>
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: possuiArendatarioComodatario ? 4 : 6 }} display="flex" justifyContent="flex-start">
                            <Controller
                                name={`arrendatario`}
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field }) => (
                                    <FormControl {...field}>
                                        <FormLabel id="arrendatario">Arrendatário/Comodatário?</FormLabel>
                                        <RadioGroup
                                            row
                                            aria-labelledby="arrendatario"
                                            value={getValues(`arrendatario`)}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                        >
                                            {
                                                opcoesRadio.map((opcao, index) => (
                                                    <FormControlLabel key={index} value={opcao.value} control={<Radio />} label={opcao.label} />
                                                ))
                                            }
                                        </RadioGroup>
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        {
                            possuiArendatarioComodatario &&
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Controller
                                    name={`vencimentoContrato`}
                                    control={control}
                                    rules={{
                                        required: possuiArendatarioComodatario ? 'Campo obrigatório' : false,
                                        validate: (value) => {
                                            if (!value) return possuiArendatarioComodatario ? 'Campo obrigatório' : true;
                                            const selectedDate = moment(value);
                                            const today = moment().startOf('day');
                                            if (selectedDate.isBefore(today)) {
                                                return 'A data de vencimento não pode ser no passado';
                                            }
                                            return true;
                                        }
                                    }}
                                    render={({ field, fieldState: { error, invalid } }) => (
                                        <DatePicker
                                            {...field}
                                            sx={{ width: '100%' }}
                                            label="Vencimento contrato"
                                            onChange={(data) => field.onChange(data as Moment)}
                                            minDate={moment()}
                                            slotProps={{
                                                textField: {
                                                    error: invalid,
                                                    helperText: error?.message || ""
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                        }

                        <Grid size={{ xs: 12, md: possuiArendatarioComodatario ? 4 : 6 }}>
                            <Controller
                                name={`tamanho`}
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <TextField
                                        {...field}
                                        required
                                        label="Tamanho (Ha)"
                                        variant="outlined"
                                        type="number"
                                        fullWidth
                                        error={invalid}
                                        helperText={error?.message || ""}
                                    />
                                )}
                            />
                        </Grid>

                        <RowRadio
                            {...{ opcoesRadio }}
                            inputLabelValor
                            defaultValue={getValues(`leite.produtor`)}
                            radioInput={{
                                controlName: "leite.produtor",
                                label: "Produtor leite?"
                            }}
                            inputLabel01={{
                                controlName: "leite.mediaDiaria",
                                label: "Média diária"
                            }}
                            inputLabel02={{
                                controlName: "leite.qtdVacas",
                                label: "Qtd. vacas"
                            }}
                        />

                        <RowRadio
                            {...{ opcoesRadio }}
                            inputLabelValor
                            defaultValue={getValues(`cafe.produtor`)}
                            radioInput={{
                                controlName: "cafe.produtor",
                                label: "Produtor café?"
                            }}
                            inputLabel01={{
                                controlName: "cafe.qtdSacas",
                                label: "Qtd. sacas"
                            }}
                            inputLabel02={{
                                controlName: "cafe.qtdHa",
                                label: "Qtd. Ha"
                            }}
                        />

                        <RowRadio
                            {...{ opcoesRadio }}
                            inputLabelValor
                            defaultValue={getValues(`milho.produtor`)}
                            radioInput={{
                                controlName: "milho.produtor",
                                label: "Produtor milho?"
                            }}
                            inputLabel01={{
                                controlName: "milho.qtdSacas",
                                label: "Qtd. sacas"
                            }}
                            inputLabel02={{
                                controlName: "milho.qtdHa",
                                label: "Qtd. Ha"
                            }}
                        />

                        <RowRadio
                            {...{ opcoesRadio }}
                            inputLabelValor
                            defaultValue={getValues(`soja.produtor`)}
                            radioInput={{
                                controlName: "soja.produtor",
                                label: "Produtor soja?"
                            }}
                            inputLabel01={{
                                controlName: "soja.qtdSacas",
                                label: "Qtd. sacas"
                            }}
                            inputLabel02={{
                                controlName: "soja.qtdHa",
                                label: "Qtd. Ha"
                            }}
                        />

                        <RowRadio
                            {...{ opcoesRadio }}
                            inputLabelValor
                            defaultValue={getValues(`gadoCorte.produtor`)}
                            radioInput={{
                                controlName: "gadoCorte.produtor",
                                label: "Possui gado corte?"
                            }}
                            inputLabel01={{
                                controlName: "gadoCorte.qtdCabecas",
                                label: "Qtd. cabeças"
                            }}
                            inputLabel02={{
                                controlName: "gadoCorte.raca",
                                label: "Raça",
                                textField: true
                            }}
                        />

                        <RowRadio
                            {...{ opcoesRadio }}
                            inputLabelValor
                            defaultValue={getValues(`outraCultura.produtor`)}
                            radioInput={{
                                controlName: "outraCultura.produtor",
                                label: "Possui outra cultura?"
                            }}
                            inputLabel01={{
                                controlName: "outraCultura.nome",
                                label: "Qual",
                                textField: true
                            }}
                            inputLabel02={{
                                controlName: "outraCultura.producaoEstimada",
                                label: "Produção"
                            }}
                        />

                        <RowRadio
                            {...{ opcoesRadio }}
                            defaultValue={getValues(`outraRenda.produtor`)}
                            radioInput={{
                                controlName: "outraRenda.produtor",
                                label: "Possui outra renda?"
                            }}
                            inputLabel01={{
                                controlName: "outraRenda.nome",
                                label: "Qual",
                                textField: true
                            }}
                            inputLabel02={{
                                controlName: "outraRenda.valor",
                                label: "Valor"
                            }}
                        />

                    </Grid>
                    <DialogActions>
                        <Button onClick={onClose}>Cancelar</Button>
                        <Button variant="contained" color="primary"
                            onClick={handleSubmit((data) => onSubmit(data as TDadosPropriedade))}
                        >Salvar</Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>
        </FormProvider>
    )
}

function RowRadio({
    defaultValue,
    opcoesRadio,
    radioInput,
    inputLabel01,
    inputLabel02,
    inputLabelValor = false,
}: RowRadioProps) {

    const { control, watch } = useFormContext();

    const possuiItem = watch(`${radioInput.controlName}`) === "Sim"
    const propRoot = inputLabel01.controlName.split('.')[0]

    return (
        <Grid container size={{ xs: 12 }}>
            <Grid container size={{ xs: 12, md: 6 }} display="flex" alignItems="center">
                <Grid size={{ xs: 12, md: 6 }}>
                    <Typography>{radioInput.label}</Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                        name={`${radioInput.controlName}`}
                        control={control}
                        rules={{ required: 'Campo obrigatório' }}
                        render={({ field }) => (
                            <FormControl {...field}>
                                <RadioGroup
                                    row
                                    value={defaultValue}
                                >
                                    {
                                        opcoesRadio.map((opcao, index) => (
                                            <FormControlLabel key={index} value={opcao.value} control={<Radio />} label={opcao.label} />
                                        ))
                                    }
                                </RadioGroup>
                            </FormControl>
                        )}
                    />
                </Grid>
            </Grid>

            <Grid container size={{ xs: 12, md: 6 }} spacing={1}>
                <Grid size={{ xs: 12, md: inputLabelValor ? 4 : 6 }}>
                    <Controller
                        name={`${inputLabel01.controlName}`}
                        rules={{ required: possuiItem ? 'Campo obrigatório' : false }}
                        control={control}
                        render={({ field, fieldState: { error, invalid } }) => {
                            return (
                                <TextField
                                    {...field}
                                    label={inputLabel01.label}
                                    variant="outlined"
                                    type={inputLabel01.textField ? "text" : "number"}
                                    fullWidth
                                    error={invalid}
                                    helperText={error?.message || ""}
                                />
                            )
                        }}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: inputLabelValor ? 4 : 6 }}>
                    <Controller
                        name={`${inputLabel02.controlName}`}
                        rules={{ required: possuiItem ? 'Campo obrigatório' : false }}
                        control={control}
                        render={({ field, fieldState: { error, invalid } }) => {
                            return (
                                <TextField
                                    {...field}
                                    label={inputLabel02.label}
                                    variant="outlined"
                                    type={inputLabel02.textField ? "text" : "number"}
                                    fullWidth
                                    error={invalid}
                                    helperText={error?.message || ""}
                                />
                            )
                        }}
                    />
                </Grid>

                {
                    inputLabelValor &&
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Controller
                            name={`${propRoot}.valor`}
                            rules={{ required: possuiItem ? 'Campo obrigatório' : false }}
                            control={control}
                            render={({ field, fieldState: { error, invalid } }) => {
                                return (
                                    <TextField
                                        {...field}
                                        label="Valor"
                                        variant="outlined"
                                        fullWidth
                                        onChange={(e) => field.onChange(formatarInputValorMonetário(e.target.value))}
                                        error={invalid}
                                        helperText={error?.message || ""}
                                    />
                                )
                            }}
                        />
                    </Grid>
                }
            </Grid>
        </Grid>
    )
}
interface DialogFormDadosPropriedadeProps {
    defaultValue: TOpenModalPropriedade
    open: boolean;
    onClose: () => void;
    onSubmit: (data: TDadosPropriedade) => void;
}
interface RowRadioProps {
    defaultValue: string,
    opcoesRadio: Array<{
        value: string,
        label: string
    }>,
    radioInput: TInputControl,
    inputLabel01: TInputControl,
    inputLabel02: TInputControl
    inputLabelValor?: boolean
}

type TInputControl = {
    label: string
    controlName: string
    textField?: boolean
}